import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FilterNode, SortSpec } from '@aurora/contracts';
import { del, get, patch, post, type ApiError } from '../../lib/api';
import { savedViewsKey } from './list-cache';

/**
 * Saved views of the ticket list: `GET/POST/PATCH/DELETE /views`.
 *
 * The server has had these, with seven built-in queues, since the first
 * release; nothing in the interface used them, so the only way to keep a
 * filter was to bookmark its URL.
 */

export interface SavedView {
  id: number;
  name: string;
  description: string | null;
  resource: string;
  /** As stored: may be `{}` for a view saved without a filter. */
  filter: unknown;
  search_query: string | null;
  sort: unknown;
  columns: string[] | null;
  visibility: 'private' | 'shared' | 'role' | 'group' | string;
  is_system: boolean;
  is_default: boolean;
  is_pinned: boolean;
  is_mine: boolean;
  owner: { id: number; name: string } | null;
}

export interface ViewDraft {
  name: string;
  description?: string | null;
  filter?: FilterNode;
  search_query?: string | null;
  sort?: SortSpec[];
  columns?: string[];
  visibility: 'private' | 'shared';
}

export function useSavedViews(enabled = true) {
  return useQuery<{ data: SavedView[] }, ApiError, SavedView[]>({
    queryKey: savedViewsKey,
    queryFn: () => get('/views', { resource: 'tickets' }),
    select: (response) => response.data,
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Whoever owns a view may change it; anybody else needs `view.edit_shared`, as the server checks. */
export function canEditView(view: SavedView, can: (permission: string) => boolean): boolean {
  return view.is_mine || can('view.edit_shared');
}

/** Built-in views are hidden rather than deleted; the server refuses the delete. */
export function canDeleteView(view: SavedView, can: (permission: string) => boolean): boolean {
  return !view.is_system && canEditView(view, can);
}

export function useViewMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: savedViewsKey });

  const create = useMutation<{ data: { id: number; name: string } }, ApiError, ViewDraft>({
    mutationFn: (draft) => post('/views', { resource: 'tickets', ...draft }),
    onSuccess: () => void refresh(),
  });

  /**
   * `visibility` is always sent. The server's schema gives it a default that
   * survives a partial update, so a rename that left it out made a shared view
   * private for everyone else — and failed outright on a built-in view.
   */
  const update = useMutation<unknown, ApiError, { id: number; changes: Partial<ViewDraft> & { visibility: string } }>({
    mutationFn: ({ id, changes }) => patch(`/views/${id}`, changes),
    onSuccess: () => void refresh(),
  });

  const remove = useMutation<unknown, ApiError, number>({
    mutationFn: (id) => del(`/views/${id}`),
    onSuccess: () => void refresh(),
  });

  /** Pinning shows in place at once, and is put back if the server refuses. */
  const pin = useMutation<unknown, ApiError, { id: number; pinned: boolean }, { previous?: { data: SavedView[] } }>({
    mutationFn: ({ id, pinned }) => (pinned ? post(`/views/${id}/pin`) : del(`/views/${id}/pin`)),
    onMutate: async ({ id, pinned }) => {
      await queryClient.cancelQueries({ queryKey: savedViewsKey });
      const previous = queryClient.getQueryData<{ data: SavedView[] }>(savedViewsKey);
      if (previous) {
        queryClient.setQueryData(savedViewsKey, {
          ...previous,
          data: previous.data.map((view) => (view.id === id ? { ...view, is_pinned: pinned } : view)),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(savedViewsKey, context.previous);
    },
    onSettled: () => void refresh(),
  });

  return { create, update, remove, pin };
}
